import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Heart, MessageCircle, Trash2, Send } from 'lucide-react';
import SafetyMenu from './SafetyMenu';
import ReportModal from './ReportModal';
import StoriesCarousel from './StoriesCarousel';
import MentionAutocomplete from './MentionAutocomplete';

const PostCard = ({ post, liked, currentUserId, token, API, t, isRTL, onChanged, onOpenProfile }) => {
  const [isLiked, setIsLiked] = useState(!!liked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const commentInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null); // { type: 'post'|'comment', id }
  const [hidden, setHidden] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const blockUser = async (uid) => {
    if (!window.confirm(t('blockConfirm'))) return;
    try {
      await axios.post(`${API}/users/${uid}/block`, {}, { headers });
      setHidden(true);
      onChanged && onChanged();
    } catch {
      alert(t('error'));
    }
  };

  const removeComment = (commentId) => {
    setComments((arr) => arr.filter((c) => c.id !== commentId));
  };

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    const next = !isLiked;
    setIsLiked(next);
    setLikesCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      if (next) {
        await axios.post(`${API}/posts/${post.id}/like`, {}, { headers });
      } else {
        await axios.delete(`${API}/posts/${post.id}/like`, { headers });
      }
    } catch (e) {
      // revert
      setIsLiked(!next);
      setLikesCount((c) => Math.max(0, c + (next ? -1 : 1)));
    } finally {
      setBusy(false);
    }
  };

  const loadComments = async () => {
    try {
      const res = await axios.get(`${API}/posts/${post.id}/comments`, { headers });
      setComments(res.data || []);
    } catch (e) {
      setComments([]);
    }
  };

  const openComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next) await loadComments();
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/posts/${post.id}/comments`,
        { text: commentText.trim() },
        { headers }
      );
      setCommentText('');
      await loadComments();
    } finally {
      setBusy(false);
    }
  };

  const deletePost = async () => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API}/posts/${post.id}`, { headers });
      onChanged && onChanged();
    } catch (e) {
      alert(t('error'));
    }
  };

  const isOwner = currentUserId === post.user_id;
  const isVideo = post.media_type === 'video';

  if (hidden) return null;

  return (
    <article
      data-testid={`post-card-${post.id}`}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4"
    >
      <header className="flex items-center justify-between px-4 py-3">
        <button
          onClick={() => onOpenProfile && onOpenProfile(post.user_id)}
          className="flex items-center gap-3"
          data-testid={`post-author-${post.id}`}
        >
          <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold">
            {(post.user_name || '?').charAt(0).toUpperCase()}
          </div>
          <span className="font-semibold text-gray-900">{post.user_name}</span>
        </button>
        <div className="flex items-center gap-1">
          {isOwner && (
            <button
              onClick={deletePost}
              data-testid={`post-delete-${post.id}`}
              className="text-gray-400 hover:text-red-500 p-1"
              aria-label={t('deletePost')}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          {!isOwner && (
            <SafetyMenu
              testid={`post-safety-${post.id}`}
              t={t}
              isRTL={isRTL}
              align="end"
              size="md"
              items={[
                {
                  key: 'report',
                  label: t('reportPost'),
                  danger: true,
                  onClick: () => setReport({ type: 'post', id: post.id }),
                  testid: `post-safety-report-${post.id}`,
                },
                {
                  key: 'block',
                  label: t('block'),
                  danger: true,
                  onClick: () => blockUser(post.user_id),
                  testid: `post-safety-block-${post.id}`,
                },
              ]}
            />
          )}
        </div>
      </header>

      <div className="bg-gray-100 max-h-[600px] overflow-hidden flex items-center justify-center">
        {isVideo ? (
          <video
            src={post.media_url}
            controls
            className="w-full max-h-[600px] object-contain bg-black"
          />
        ) : (
          <img
            src={post.media_url}
            alt={post.caption || 'post'}
            className="w-full max-h-[600px] object-contain"
          />
        )}
      </div>

      <div className="px-4 pt-3 pb-2 flex items-center gap-4">
        <button
          onClick={toggleLike}
          data-testid={`post-like-${post.id}`}
          className={`flex items-center gap-1.5 transition-colors ${
            isLiked ? 'text-red-500' : 'text-gray-700 hover:text-red-500'
          }`}
          disabled={busy}
        >
          <Heart className="w-6 h-6" fill={isLiked ? 'currentColor' : 'none'} />
          <span className="text-sm font-medium">{likesCount}</span>
        </button>
        <button
          onClick={openComments}
          data-testid={`post-comments-toggle-${post.id}`}
          className="flex items-center gap-1.5 text-gray-700 hover:text-purple-600"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="text-sm font-medium">{post.comments_count || 0}</span>
        </button>
      </div>

      {post.caption && (
        <p className="px-4 pb-3 text-sm text-gray-800">
          <span className="font-semibold mr-1">{post.user_name}</span>
          {post.caption}
        </p>
      )}

      {showComments && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          <ul className="space-y-2 mb-3 max-h-60 overflow-y-auto">
            {comments.length === 0 && (
              <li className="text-sm text-gray-500">—</li>
            )}            {comments.map((c) => (
              <li
                key={c.id}
                data-testid={`post-comment-${c.id}`}
                className="text-sm flex items-start justify-between gap-2"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-semibold mr-1">{c.user_name}</span>
                  <span className="text-gray-800">{c.text}</span>
                </div>
                {c.user_id !== currentUserId && (
                  <SafetyMenu
                    testid={`comment-safety-${c.id}`}
                    t={t}
                    isRTL={isRTL}
                    align="end"
                    size="sm"
                    items={[
                      {
                        key: 'report',
                        label: t('reportComment'),
                        danger: true,
                        onClick: () => setReport({ type: 'comment', id: c.id }),
                        testid: `comment-safety-report-${c.id}`,
                      },
                      {
                        key: 'block',
                        label: t('block'),
                        danger: true,
                        onClick: async () => {
                          await blockUser(c.user_id);
                          removeComment(c.id);
                        },
                        testid: `comment-safety-block-${c.id}`,
                      },
                    ]}
                  />
                )}
              </li>
            ))}
          </ul>
          <form onSubmit={submitComment} className="flex gap-2 relative" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex-1 relative">
              <MentionAutocomplete
                inputRef={commentInputRef}
                value={commentText}
                onChange={(next) => setCommentText(next)}
                token={token}
                API={API}
                isRTL={isRTL}
                testIdPrefix={`comment-mention-${post.id}`}
              />
              <input
                ref={commentInputRef}
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t('addComment')}
                data-testid={`post-comment-input-${post.id}`}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400 bg-white text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={!commentText.trim() || busy}
              data-testid={`post-comment-submit-${post.id}`}
              className="px-3 py-2 rounded-lg bg-purple-600 text-white disabled:opacity-50 hover:bg-purple-700 flex items-center gap-1"
            >
              <Send className="w-4 h-4" />
              <span className="text-sm">{t('sendComment')}</span>
            </button>
          </form>
        </div>
      )}

      <ReportModal
        open={!!report}
        onClose={() => setReport(null)}
        targetType={report?.type || 'post'}
        targetId={report?.id || ''}
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
      />
    </article>
  );
};

const PAGE_SIZE = 20;
const REFRESH_THRESHOLD = 70;

const Feed = ({ token, API, currentUser, t, isRTL, onOpenProfile, onGoToExplore, refreshKey }) => {
  const [source, setSource] = useState('all');
  const [posts, setPosts] = useState([]);
  const [likedIds, setLikedIds] = useState(new Set());
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDist, setPullDist] = useState(0);

  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const cursorRef = useRef(null);
  const hasMoreRef = useRef(false);
  const sourceRef = useRef('all');

  const headers = { Authorization: `Bearer ${token}` };

  const loadFirstPage = useCallback(
    async (src, asRefresh = false) => {
      if (asRefresh) setRefreshing(true); else setLoading(true);
      try {
        const res = await axios.get(`${API}/feed`, {
          params: { limit: PAGE_SIZE, source: src },
          headers,
        });
        setPosts(res.data.posts || []);
        setLikedIds(new Set(res.data.liked_post_ids || []));
        setCursor(res.data.next_cursor || null);
        setHasMore(!!res.data.has_more);
      } catch {
        setPosts([]);
        setCursor(null);
        setHasMore(false);
      } finally {
        if (asRefresh) setRefreshing(false); else setLoading(false);
      }
    },
    [API, token]
  );

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    if (!hasMoreRef.current || !cursorRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/feed`, {
        params: { limit: PAGE_SIZE, source: sourceRef.current, cursor: cursorRef.current },
        headers,
      });
      setPosts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const newOnes = (res.data.posts || []).filter((p) => !seen.has(p.id));
        return [...prev, ...newOnes];
      });
      setLikedIds((prev) => {
        const next = new Set(prev);
        (res.data.liked_post_ids || []).forEach((id) => next.add(id));
        return next;
      });
      setCursor(res.data.next_cursor || null);
      setHasMore(!!res.data.has_more);
    } catch {
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [API, token]);

  // Refs mirror state for the IntersectionObserver closure
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { sourceRef.current = source; }, [source]);

  // Initial load + on source/refreshKey change
  useEffect(() => {
    loadFirstPage(source);
  }, [source, refreshKey, loadFirstPage]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '300px' }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [loadMore, posts.length]);

  // Pull-to-refresh (touch) — only when at scrollTop=0
  useEffect(() => {
    let startY = null;
    let activated = false;

    const onTouchStart = (e) => {
      if (window.scrollY <= 0 && !refreshing) {
        startY = e.touches[0].clientY;
        activated = true;
      }
    };
    const onTouchMove = (e) => {
      if (!activated || startY === null) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > 0) {
        setPullDist(Math.min(dy, REFRESH_THRESHOLD * 1.5));
      }
    };
    const onTouchEnd = () => {
      if (!activated) return;
      const wasOver = pullDist >= REFRESH_THRESHOLD;
      setPullDist(0);
      activated = false;
      startY = null;
      if (wasOver) {
        loadFirstPage(sourceRef.current, true);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [pullDist, refreshing, loadFirstPage]);

  const onCardChanged = () => {
    // Only invoked by delete/comment actions that don't need a full reload.
    // Local mutations keep counters fresh; nothing to do here for now.
  };

  const Toggle = (
    <div
      data-testid="feed-source-toggle"
      className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 border-b border-gray-100"
    >
      <div className="max-w-xl mx-auto px-3 py-2.5 flex bg-gray-100 rounded-lg p-1 text-sm font-semibold mt-2 mb-2">
        {[
          { k: 'all', label: t('feedTabForYou') },
          { k: 'following', label: t('feedTabFollowing') },
        ].map(({ k, label }) => (
          <button
            key={k}
            type="button"
            onClick={() => setSource(k)}
            data-testid={`feed-tab-${k}`}
            className={`flex-1 py-1.5 rounded-md transition-colors ${
              source === k ? 'bg-white text-purple-700 shadow' : 'text-gray-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  const PullSpinner = (refreshing || pullDist > 0) ? (
    <div
      data-testid="feed-pull-indicator"
      className="flex items-center justify-center text-xs text-gray-500 transition-all"
      style={{ height: refreshing ? 36 : Math.min(pullDist, 60) }}
    >
      {refreshing
        ? t('refreshing')
        : pullDist >= REFRESH_THRESHOLD
        ? t('releaseToRefresh')
        : t('pullToRefresh')}
    </div>
  ) : null;

  return (
    <div data-testid="feed-root">
      <StoriesCarousel
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
        currentUser={currentUser}
      />
      {Toggle}
      {PullSpinner}

      {loading ? (
        <div data-testid="feed-loading" className="flex items-center justify-center py-20 text-gray-500">
          {t('loading')}
        </div>
      ) : posts.length === 0 ? (
        source === 'following' ? (
          <div
            data-testid="feed-following-empty"
            className="flex flex-col items-center justify-center py-20 text-gray-600 px-6 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white text-2xl font-bold mb-4">
              ◎
            </div>
            <p className="text-base font-semibold mb-1">{t('feedFollowingEmpty')}</p>
            <p className="text-sm text-gray-500 mb-4">{t('feedFollowingEmptyHint')}</p>
            <button
              type="button"
              onClick={onGoToExplore}
              data-testid="feed-following-explore-cta"
              className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold shadow"
            >
              {t('feedFollowingEmptyCTA')}
            </button>
          </div>
        ) : (
          <div
            data-testid="feed-empty"
            className="flex flex-col items-center justify-center py-20 text-gray-500 px-6 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white text-2xl font-bold mb-4">
              ◎
            </div>
            <p className="text-base">{t('feedEmpty')}</p>
          </div>
        )
      ) : (
        <div data-testid="feed-list" className="max-w-xl mx-auto px-3 pt-4 pb-4">
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              liked={likedIds.has(p.id)}
              currentUserId={currentUser?.id}
              token={token}
              API={API}
              t={t}
              isRTL={isRTL}
              onChanged={onCardChanged}
              onOpenProfile={onOpenProfile}
            />
          ))}
          {hasMore ? (
            <>
              <div ref={sentinelRef} data-testid="feed-sentinel" className="h-px" />
              {loadingMore && (
                <p
                  data-testid="feed-loading-more"
                  className="text-center text-xs text-gray-500 py-3"
                >
                  {t('loadingMore')}
                </p>
              )}
            </>
          ) : (
            <p
              data-testid="feed-end"
              className="text-center text-xs text-gray-400 py-6"
            >
              {t('youReachedEnd')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Feed;
